import type { RectangleShape, TeacherAnnotation, TextAnnotation } from "./teacherCorrectionTypes";

export type RectCorner = "nw" | "ne" | "sw" | "se";

export function normalizeRect(
  x0: number,
  y0: number,
  x1: number,
  y1: number
): { x: number; y: number; w: number; h: number } {
  const x = Math.min(x0, x1);
  const y = Math.min(y0, y1);
  const w = Math.abs(x1 - x0);
  const h = Math.abs(y1 - y0);
  return { x, y, w, h };
}

export function pointInRect(px: number, py: number, r: RectangleShape): boolean {
  return (
    px >= r.x &&
    py >= r.y &&
    px <= r.x + r.width &&
    py <= r.y + r.height
  );
}

/** Rough text block size for hit-testing (matches drawTextAnnotation line layout). */
export function estimateTextBounds(t: TextAnnotation): { x: number; y: number; w: number; h: number } {
  const lines = (t.text || " ").split("\n");
  const lh = t.fontSize * 1.35;
  const charW = t.fontSize * 0.55;
  const maxLen = Math.max(1, ...lines.map((l) => l.length));
  const w = Math.max(t.width ?? 0, Math.min(400, maxLen * charW + 16), 48);
  const h = Math.max(t.height ?? 0, lines.length * lh + 8);
  return { x: t.x, y: t.y, w, h };
}

export function exportSlopFromCanvas(canvasW: number, displayW: number): number {
  if (displayW <= 0) return 12;
  return (12 * canvasW) / displayW;
}

export function hitTestRectCorner(
  px: number,
  py: number,
  r: RectangleShape,
  slop: number
): RectCorner | null {
  const corners: { id: RectCorner; cx: number; cy: number }[] = [
    { id: "nw", cx: r.x, cy: r.y },
    { id: "ne", cx: r.x + r.width, cy: r.y },
    { id: "sw", cx: r.x, cy: r.y + r.height },
    { id: "se", cx: r.x + r.width, cy: r.y + r.height },
  ];
  for (const c of corners) {
    if (Math.hypot(px - c.cx, py - c.cy) <= slop) return c.id;
  }
  return null;
}

export function hitTestAnnotations(
  px: number,
  py: number,
  annotations: TeacherAnnotation[],
  selectedId: string | null,
  slop: number
): { id: string; kind: "rectangle" | "text"; hit: "handle" | "body"; handle?: RectCorner } | null {
  if (selectedId) {
    const sel = annotations.find((a) => a.id === selectedId);
    if (sel?.tool === "rectangle") {
      const h = hitTestRectCorner(px, py, sel, slop);
      if (h) return { id: sel.id, kind: "rectangle", hit: "handle", handle: h };
    }
  }
  for (let i = annotations.length - 1; i >= 0; i--) {
    const a = annotations[i];
    if (a.tool === "rectangle") {
      if (pointInRect(px, py, a)) return { id: a.id, kind: "rectangle", hit: "body" };
    } else if (a.tool === "text") {
      const b = estimateTextBounds(a);
      if (px >= b.x && py >= b.y && px <= b.x + b.w && py <= b.y + b.h) {
        return { id: a.id, kind: "text", hit: "body" };
      }
    }
  }
  return null;
}

export function resizeRectangle(
  r: RectangleShape,
  handle: RectCorner,
  px: number,
  py: number,
  minSize: number
): RectangleShape {
  let { x, y, width: w, height: h } = r;
  const x1 = x + w;
  const y1 = y + h;
  switch (handle) {
    case "se":
      w = Math.max(minSize, px - x);
      h = Math.max(minSize, py - y);
      break;
    case "nw": {
      const nx1 = x1;
      const ny1 = y1;
      x = Math.min(px, nx1 - minSize);
      y = Math.min(py, ny1 - minSize);
      w = nx1 - x;
      h = ny1 - y;
      break;
    }
    case "ne": {
      const nx = x;
      const ny1 = y1;
      y = Math.min(py, ny1 - minSize);
      w = Math.max(minSize, px - nx);
      h = ny1 - y;
      break;
    }
    case "sw": {
      const nx1 = x1;
      const ny = y;
      x = Math.min(px, nx1 - minSize);
      w = nx1 - x;
      h = Math.max(minSize, py - ny);
      break;
    }
    default:
      break;
  }
  return { ...r, x, y, width: w, height: h };
}
